import apiClient from './apiClient';

// Types for clients data
export interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: {
    street: string;
    city: string;
    county: string;
    postalCode: string;
    country: string;
  };
  dateOfBirth?: string;
  cnp?: string;
  status: 'active' | 'inactive' | 'suspended';
  tags: string[];
  gdprConsent: boolean;
  communicationPreferences: {
    sms: boolean;
    email: boolean;
    whatsapp: boolean;
  };
  createdAt: string;
  updatedAt: string;
  clinicId: string;
  // Computed fields
  patientCount: number;
  lastVisit?: string;
  currentBalance: number;
  totalSpent: number;
  noShowRate: number;
}

export interface ClientFilters {
  search?: string;
  status?: 'active' | 'inactive' | 'suspended' | 'all';
  hasOutstanding?: boolean;
  minPatients?: number;
  maxPatients?: number;
  tags?: string[];
  createdFrom?: string;
  createdTo?: string;
  clinicId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'createdAt' | 'lastVisit' | 'balance' | 'patientCount';
  sortOrder?: 'asc' | 'desc';
}

export interface ClientDetail extends Client {
  patients: Array<{
    id: string;
    name: string;
    species: string;
    breed: string;
    age: number;
    status: 'active' | 'deceased' | 'transferred';
  }>;
  payments: Array<{
    id: string;
    date: string;
    amount: number;
    method: 'cash' | 'card' | 'transfer';
    status: 'paid' | 'pending' | 'cancelled';
    documentType: 'receipt' | 'invoice' | 'proforma';
    reference: string;
  }>;
  appointments: Array<{
    id: string;
    date: string;
    time: string;
    patientName: string;
    service: string;
    veterinarian: string;
    status: 'scheduled' | 'confirmed' | 'present' | 'no-show' | 'cancelled';
  }>;
  documents: Array<{
    id: string;
    name: string;
    type: 'contract' | 'consent' | 'identity' | 'other';
    uploadDate: string;
    fileSize: number;
    url: string;
  }>;
  communications: Array<{
    id: string;
    type: 'sms' | 'email' | 'whatsapp';
    content: string;
    sentAt: string;
    status: 'sent' | 'delivered' | 'read' | 'failed';
    response?: string;
  }>;
  notes: Array<{
    id: string;
    content: string;
    author: string;
    createdAt: string;
    isInternal: boolean;
  }>;
  timeline: Array<{
    id: string;
    type: 'appointment' | 'payment' | 'communication' | 'document' | 'note';
    title: string;
    description: string;
    date: string;
    metadata?: any;
  }>;
}

export interface CreateClientData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address?: {
    street: string;
    city: string;
    county: string;
    postalCode: string;
    country: string;
  };
  dateOfBirth?: string;
  cnp?: string;
  tags: string[];
  gdprConsent: boolean;
  communicationPreferences: {
    sms: boolean;
    email: boolean;
    whatsapp: boolean;
  };
}

export interface UpdateClientData extends Partial<CreateClientData> {
  status?: 'active' | 'inactive' | 'suspended';
}

export interface ClientStats {
  total: number;
  active: number;
  inactive: number;
  suspended: number;
  withOutstanding: number;
  averageLTV: number;
  averageNoShowRate: number;
}

class ClientsService {
  async getClients(filters: ClientFilters = {}): Promise<{
    clients: Client[];
    total: number;
    page: number;
    limit: number;
    stats: ClientStats;
  }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.hasOutstanding !== undefined) params.append('hasOutstanding', filters.hasOutstanding.toString());
      if (filters.minPatients !== undefined) params.append('minPatients', filters.minPatients.toString());
      if (filters.maxPatients !== undefined) params.append('maxPatients', filters.maxPatients.toString());
      if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
      if (filters.createdFrom) params.append('createdFrom', filters.createdFrom);
      if (filters.createdTo) params.append('createdTo', filters.createdTo);
      if (filters.clinicId) params.append('clinicId', filters.clinicId);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await apiClient.get(`/clients?${params}`);

      // Transform backend response to match expected interface
      const backendData = response.data.data;
      return {
        clients: backendData.clients || [],
        total: backendData.pagination?.total || 0,
        page: backendData.pagination?.page || 1,
        limit: backendData.pagination?.limit || 10,
        stats: backendData.stats || {
          total: 0,
          active: 0,
          inactive: 0,
          suspended: 0,
          withOutstanding: 0,
          averageLTV: 0,
          averageNoShowRate: 0
        }
      };
    } catch (error) {
      console.error('Error fetching clients:', error);
      return {
        clients: [],
        total: 0,
        page: 1,
        limit: 10,
        stats: {
          total: 0,
          active: 0,
          inactive: 0,
          suspended: 0,
          withOutstanding: 0,
          averageLTV: 0,
          averageNoShowRate: 0
        }
      };
    }
  }

  async getClientById(id: string): Promise<ClientDetail | null> {
    try {
      const response = await apiClient.get(`/clients/${id}`);

      return response.data;
    } catch (error) {
      console.error('Error fetching client details:', error);
      return null;
    }
  }

  async createClient(clientData: CreateClientData): Promise<Client> {
    try {
      const response = await apiClient.post('/clients', clientData);
      if (response.data?.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data?.error || 'Eroare la crearea clientului');
    } catch (error: any) {
      const msg = error?.response?.data?.error || error?.message || 'Eroare la crearea clientului';
      throw new Error(msg);
    }
  }

  async updateClient(id: string, clientData: UpdateClientData): Promise<Client | null> {
    try {
      const response = await apiClient.put(`/clients/${id}`, clientData);

      return response.data;
    } catch (error) {
      console.error('Error updating client:', error);
      return null;
    }
  }

  async deleteClient(id: string): Promise<boolean> {
    try {
      await apiClient.delete(`/clients/${id}`);

      return true;
    } catch (error) {
      console.error('Error deleting client:', error);
      return false;
    }
  }

  async bulkUpdateClients(ids: string[], updates: UpdateClientData): Promise<boolean> {
    try {
      await apiClient.patch('/clients/bulk', {
        ids,
        updates
      });

      return true;
    } catch (error) {
      console.error('Error bulk updating clients:', error);
      return false;
    }
  }

  async sendMessage(clientIds: string[], type: 'sms' | 'email' | 'whatsapp', content: string): Promise<boolean> {
    try {
      await apiClient.post('/clients/communications', {
        clientIds,
        type,
        content
      });

      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  }

  async createInvoice(clientId: string, items: Array<{
    description: string;
    quantity: number;
    price: number;
    vat: number;
  }>): Promise<{ invoiceId: string; invoiceNumber: string } | null> {
    try {
      const response = await apiClient.post(`/clients/${clientId}/invoices`, {
        items
      });

      return response.data;
    } catch (error) {
      console.error('Error creating invoice:', error);
      return null;
    }
  }

  async createPaymentNote(clientId: string, amount: number, description: string): Promise<{ noteId: string } | null> {
    try {
      const response = await apiClient.post(`/clients/${clientId}/payment-notes`, {
        amount,
        description
      });

      return response.data;
    } catch (error) {
      console.error('Error creating payment note:', error);
      return null;
    }
  }

  async addInternalNote(clientId: string, content: string): Promise<boolean> {
    try {
      await apiClient.post(`/clients/${clientId}/notes`, {
        content,
        isInternal: true
      });

      return true;
    } catch (error) {
      console.error('Error adding internal note:', error);
      return false;
    }
  }

  async updateGDPRConsent(clientId: string, consent: boolean): Promise<boolean> {
    try {
      await apiClient.patch(`/clients/${clientId}/gdpr-consent`, {
        consent
      });

      return true;
    } catch (error) {
      console.error('Error updating GDPR consent:', error);
      return false;
    }
  }

  async deactivateClient(clientId: string, reason: string): Promise<boolean> {
    try {
      await apiClient.patch(`/clients/${clientId}/deactivate`, {
        reason
      });

      return true;
    } catch (error) {
      console.error('Error deactivating client:', error);
      return false;
    }
  }

  async duplicateClient(clientId: string): Promise<Client | null> {
    try {
      const response = await apiClient.post(`/clients/${clientId}/duplicate`, {});

      return response.data;
    } catch (error) {
      console.error('Error duplicating client:', error);
      return null;
    }
  }

  async mergeClients(primaryClientId: string, secondaryClientId: string): Promise<boolean> {
    try {
      await apiClient.post('/clients/merge', {
        primaryClientId,
        secondaryClientId
      });

      return true;
    } catch (error) {
      console.error('Error merging clients:', error);
      return false;
    }
  }

  async blacklistClient(clientId: string, reason: string): Promise<boolean> {
    try {
      await apiClient.patch(`/clients/${clientId}/blacklist`, {
        reason
      });

      return true;
    } catch (error) {
      console.error('Error blacklisting client:', error);
      return false;
    }
  }

  async exportClients(filters: ClientFilters, format: 'csv' | 'xlsx'): Promise<Blob | null> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      params.append('format', format);

      const response = await apiClient.get(`/clients/export?${params}`, {
        responseType: 'blob'
      });

      return response.data;
    } catch (error) {
      console.error('Error exporting clients:', error);
      return null;
    }
  }

  async getAvailableTags(): Promise<string[]> {
    try {
      const response = await apiClient.get('/clients/tags');

      return response.data.tags || [];
    } catch (error) {
      console.error('Error fetching available tags:', error);
      return [];
    }
  }
}

export const clientsService = new ClientsService();
